// Pure rules of the meeting screens (COM-FE-05): shortcuts, the weak-link decision, who is on the
// big stage, and how the API's refusals are told to the person.
import { ApiError } from '@batiflow/client-core';
import type { ShortcutDefinition } from '@batiflow/ui';
import type { LinkQuality, RoomParticipant } from './room-engine';

/** Alt+M and Alt+O (docs/plan/13-ui-ux.md §6): they show in the shortcut help while in a room. */
export const MEETING_SHORTCUTS = {
  toggleMic: {
    id: 'meeting-toggle-mic',
    combos: [{ code: 'KeyM', alt: true }],
    labelKey: 'ui:shortcuts.toggleMic',
    group: 'meeting',
  },
  toggleCamera: {
    id: 'meeting-toggle-camera',
    combos: [{ code: 'KeyO', alt: true }],
    labelKey: 'ui:shortcuts.toggleCamera',
    group: 'meeting',
  },
} as const satisfies Record<string, ShortcutDefinition>;

/** The weak-link thresholds of docs/plan/13-ui-ux.md §2 and the module plan (COM-FE-05). */
export const POOR_FOR_MS = 8_000;
export const LOW_BANDWIDTH_KBPS = 300;
/** After "Try video again" the automatic fallback waits this long before it may switch video off. */
export const RETRY_GRACE_MS = 60_000;

export interface LinkState {
  quality: LinkQuality;
  /** Since when the quality has been poor (epoch ms), null while it is not. */
  poorSince: number | null;
  bandwidthKbps: number | null;
  now: number;
}

/** True when video should be turned off: a poor link for a while, or a very low bandwidth. */
export function shouldFallBackToAudio({ quality, poorSince, bandwidthKbps, now }: LinkState) {
  if (bandwidthKbps !== null && bandwidthKbps < LOW_BANDWIDTH_KBPS) return true;
  return (
    (quality === 'poor' || quality === 'lost') &&
    poorSince !== null &&
    now - poorSince >= POOR_FOR_MS
  );
}

/**
 * The person on the big stage in the speaker layout: someone sharing their screen, else the
 * loudest current speaker who is not the local person, else the first other person, else the
 * local person.
 */
export function pickStage(participants: readonly RoomParticipant[]): RoomParticipant | undefined {
  const sharing = participants.find((p) => p.sharingScreen);
  if (sharing) return sharing;
  const others = participants.filter((p) => !p.isLocal);
  return others.find((p) => p.speaking) ?? others[0] ?? participants[0];
}

/** What the person is told when the room refuses them (each case has its own plain message). */
export type JoinProblem =
  'full' | 'ended' | 'forbidden' | 'emailMismatch' | 'invitationInvalid' | 'unavailable' | 'other';

export function joinProblemOf(error: unknown): JoinProblem {
  if (!(error instanceof ApiError)) return 'unavailable';
  if (error.code === 'PLAN_LIMIT_REACHED') return 'full';
  if (error.code === 'MEETING_ENDED') return 'ended';
  if (error.code === 'INVITATION_EMAIL_MISMATCH') return 'emailMismatch';
  if (error.status === 404 || error.status === 410) return 'invitationInvalid';
  if (error.status === 403) return 'forbidden';
  if (error.status === 429 || error.status >= 500) return 'unavailable';
  return 'other';
}

export const meetingPath = (orgId: string, meetingId: string): string =>
  `/o/${encodeURIComponent(orgId)}/meetings/${encodeURIComponent(meetingId)}`;

export interface Announcement {
  kind: 'handRaised' | 'joined' | 'left';
  name: string;
}

/** What a screen reader should hear when the room changes: new hands, people coming and going. */
export function announcementsBetween(
  before: readonly RoomParticipant[],
  after: readonly RoomParticipant[],
): Announcement[] {
  const previous = new Map(before.map((p) => [p.identity, p]));
  const now = new Set(after.map((p) => p.identity));
  const news: Announcement[] = [];
  for (const person of after) {
    if (person.isLocal) continue;
    const was = previous.get(person.identity);
    if (!was) news.push({ kind: 'joined', name: person.name });
    else if (person.handRaised && !was.handRaised)
      news.push({ kind: 'handRaised', name: person.name });
  }
  for (const person of before) {
    if (!person.isLocal && !now.has(person.identity))
      news.push({ kind: 'left', name: person.name });
  }
  return news;
}

// --- Links and wording shared by the room, the lists and the chat (COMX-FE-02/03) --------------

const encode = encodeURIComponent;

/** The standalone room: it works with or without an organisation in the address. */
export const roomPath = (meetingId: string): string => `/meetings/${encode(meetingId)}`;

/** The address of a PV (its link; the chip navigates with the typed router). */
export const minutesPath = (spaceId: string, minutesId: string): string =>
  `/minutes/${encode(spaceId)}/${encode(minutesId)}`;

/** A duration as hours and minutes ("12 min", "1 h 05"); under a minute rounds up to one. */
export function durationParts(seconds: number): { hours: number; minutes: number } {
  const total = Math.max(1, Math.round(seconds / 60));
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}
